import {
  RESERVOIR_PREDICTIONS_FILES_URL,
  RESERVOIR_PREDICTIONS_LIST_URL,
} from "@/constants";
import {
  addDays,
  format,
  getISOWeek,
  getISOWeekYear,
  max,
  setISOWeek,
  startOfISOWeek,
} from "date-fns";
import { every, sumBy } from "lodash-es";

/**
 * Predicted reservoir level for one ISO year and week.
 *
 * @property percent - Stored energy as % of the zone's capacity
 * @property levelMwh - Stored energy in MWh; with percent it gives the capacity that weights zones in a total
 */
export interface ForecastLevel {
  year: number;
  week: number;
  percent: number;
  levelMwh: number;
}

/**
 * One earlier run of the gridworks reservoir model.
 *
 * @property forecastDate - Day the run happened (YYYY-MM-DD)
 */
export interface PreviousForecast {
  forecastDate: string;
  levels: ForecastLevel[];
}

/**
 * @property currentForecastDate - Day of the run whose forecast is in the static CSV (YYYY-MM-DD), null without runs
 * @property previous - Runs from the weeks before it, newest first
 */
export interface ForecastHistory {
  currentForecastDate: string | null;
  previous: PreviousForecast[];
}

export interface PredictionRun {
  runDate: Date;
  name: string;
}

/**
 * Which run files to read: one run per zone for each earlier week, newest first.
 *
 * @property currentForecastDate - Day of the newest run, whose forecast is in the static CSV (YYYY-MM-DD)
 */
export interface ForecastHistoryPlan {
  currentForecastDate: string | null;
  previous: { forecastDate: string; zoneRuns: PredictionRun[] }[];
}

const PREDICTIONS_PREFIX = "reservoir/predictions/predictions_";

/**
 * Fetches the forecast history for one zone, or for several zones combined (Norway total),
 * straight from the gridworks bucket.
 *
 * gridworks keeps every weekly run as `reservoir/predictions/predictions_{zone}_{YYYY_WW_DD_HH_MM}.csv`.
 * The newest run is the forecast already in the static CSV; the pipeline is sometimes re-run within
 * a week, so only each week's latest run counts. gridworks doesn't store runs for its Norway total,
 * so for several zones each week's levels are averaged weighted by zone capacity, as gridworks does
 * for its composite, using only weeks every zone has a run for.
 *
 * @throws {Error} When a bucket listing or one of the forecast files can't be fetched
 *   (an AggregateError when several failed)
 */
export const fetchForecastHistory = async (
  zones: string[],
  count: number
): Promise<ForecastHistory> => {
  const plan = planForecastHistory(
    await settleAll(zones.map(listZoneRuns)),
    count
  );
  const previous = await settleAll(
    plan.previous.map(async ({ forecastDate, zoneRuns }) => {
      const zoneLevels = await settleAll(zoneRuns.map(fetchRunLevels));
      return {
        forecastDate,
        levels:
          zoneLevels.length > 1
            ? capacityWeightedLevels(zoneLevels)
            : zoneLevels[0],
      };
    })
  );
  return { currentForecastDate: plan.currentForecastDate, previous };
};

/**
 * Picks the earlier weeks to show from each zone's latest-run-per-week list. The current week is the
 * newest run of any zone; earlier weeks must have a run in every zone, so a zone that missed or lags a
 * week drops only that week, never a newer week the zones share.
 */
export const planForecastHistory = (
  zoneRuns: PredictionRun[][],
  count: number
): ForecastHistoryPlan => {
  const newestRuns = zoneRuns.flatMap((runs) => runs.slice(0, 1));
  if (!newestRuns.length) return { currentForecastDate: null, previous: [] };
  const currentRunDate = max(newestRuns.map((run) => run.runDate));
  const currentWeek = isoWeekKey(currentRunDate);

  const [firstZone, ...otherZones] = zoneRuns;
  const previous = firstZone
    .filter((run) => isoWeekKey(run.runDate) !== currentWeek)
    .flatMap((run) => {
      const week = isoWeekKey(run.runDate);
      const weekRuns = [
        run,
        ...otherZones.map((runs) =>
          runs.find((other) => isoWeekKey(other.runDate) === week)
        ),
      ];
      if (!every(weekRuns)) return [];
      const runsOfWeek = weekRuns as PredictionRun[];
      return [
        {
          forecastDate: format(
            max(runsOfWeek.map((weekRun) => weekRun.runDate)),
            "yyyy-MM-dd"
          ),
          zoneRuns: runsOfWeek,
        },
      ];
    })
    .slice(0, count);

  return {
    currentForecastDate: format(currentRunDate, "yyyy-MM-dd"),
    previous,
  };
};

/** Latest run per ISO week, newest first: the first is the current forecast, the rest are earlier ones. */
export const latestRunPerWeek = (
  names: string[],
  prefix: string
): PredictionRun[] => {
  const runs = names
    .filter((name) => name.startsWith(prefix) && name.endsWith(".csv"))
    .map((name) => ({
      name,
      runDate: parseRunTimestamp(name.slice(prefix.length, -".csv".length)),
    }))
    .filter((run): run is PredictionRun => run.runDate !== null)
    .sort((a, b) => b.runDate.getTime() - a.runDate.getTime());

  const latestPerWeek = new Map<string, PredictionRun>();
  runs.forEach((run) => {
    if (!latestPerWeek.has(isoWeekKey(run.runDate))) {
      latestPerWeek.set(isoWeekKey(run.runDate), run);
    }
  });
  return [...latestPerWeek.values()];
};

/**
 * Inverts gridworks' `YYYY_WW_DD_HH_MM` run timestamp: calendar year, ISO week, day of month.
 * Around New Year the ISO year differs from the calendar year, so neighbouring ISO years are tried.
 */
export const parseRunTimestamp = (timestamp: string): Date | null => {
  const parts = timestamp.split("_").map(Number);
  if (parts.length !== 5 || parts.some(isNaN)) return null;
  const [year, week, day, hour, minute] = parts;

  const candidates = [year, year - 1, year + 1].flatMap((isoYear) => {
    const weekStart = startOfISOWeek(setISOWeek(new Date(isoYear, 0, 4), week));
    return [0, 1, 2, 3, 4, 5, 6].map((offset) => addDays(weekStart, offset));
  });
  const match = candidates.find(
    (date) =>
      date.getFullYear() === year &&
      date.getDate() === day &&
      getISOWeek(date) === week
  );
  return match ? new Date(year, match.getMonth(), day, hour, minute) : null;
};

/** Reads the Year, Week, Predicted_Level_MWh and Predicted_Level_Percent columns of a gridworks predictions CSV. */
export const parsePredictionsCsv = (csvText: string): ForecastLevel[] => {
  const [headerLine, ...lines] = csvText.trim().split("\n");
  const headers = headerLine.split(",").map((header) => header.trim());
  const [yearIndex, weekIndex, percentIndex, mwhIndex] = [
    "Year",
    "Week",
    "Predicted_Level_Percent",
    "Predicted_Level_MWh",
  ].map((name) => headers.indexOf(name));

  return lines
    .map((line) => line.split(","))
    .map((values) => ({
      year: parseInt(values[yearIndex]),
      week: parseInt(values[weekIndex]),
      percent: parseFloat(values[percentIndex]),
      levelMwh: parseFloat(values[mwhIndex]),
    }))
    .filter((level) => every(Object.values(level), (value) => !isNaN(value)));
};

/** Lists every page of a zone's run files and keeps the latest run per week. */
const listZoneRuns = async (zone: string): Promise<PredictionRun[]> => {
  const prefix = `${PREDICTIONS_PREFIX}${zone}_`;
  const names: string[] = [];
  let pageToken: string | undefined;
  do {
    const params = new URLSearchParams({
      prefix,
      fields: "items(name),nextPageToken",
      ...(pageToken ? { pageToken } : {}),
    });
    const page = await (
      await fetchOk(`${RESERVOIR_PREDICTIONS_LIST_URL}?${params}`)
    ).json();
    names.push(
      ...(page.items ?? []).map((item: { name: string }) => item.name)
    );
    pageToken = page.nextPageToken;
  } while (pageToken);
  return latestRunPerWeek(names, prefix);
};

const fetchRunLevels = async (run: PredictionRun): Promise<ForecastLevel[]> =>
  parsePredictionsCsv(
    await (
      await fetchOk(`${RESERVOIR_PREDICTIONS_FILES_URL}/${run.name}`)
    ).text()
  );

/** Each zone's capacity is levelMwh / percent; weighting % by it is total MWh over total capacity. */
const capacityWeightedLevels = (
  zoneLevels: ForecastLevel[][]
): ForecastLevel[] =>
  zoneLevels[0].flatMap(({ year, week }) => {
    const levels = zoneLevels.map((levels) =>
      levels.find((level) => level.year === year && level.week === week)
    );
    if (!every(levels, (level) => level && level.percent > 0)) return [];
    const weekLevels = levels as ForecastLevel[];
    const levelMwh = sumBy(weekLevels, "levelMwh");
    const capacityMwh = sumBy(
      weekLevels,
      (level) => level.levelMwh / (level.percent / 100)
    );
    return [{ year, week, levelMwh, percent: (levelMwh / capacityMwh) * 100 }];
  });

/** Waits for every promise, then throws if any failed (every reason, when several), so nothing is left in flight. */
const settleAll = async <T>(promises: Promise<T>[]): Promise<T[]> => {
  const results = await Promise.allSettled(promises);
  const reasons = results
    .filter(
      (result): result is PromiseRejectedResult => result.status === "rejected"
    )
    .map((result) => result.reason);
  if (reasons.length === 1) throw reasons[0];
  if (reasons.length > 1) {
    throw new AggregateError(
      reasons,
      `${reasons.length} of ${results.length} requests failed`
    );
  }
  return results.map((result) => (result as PromiseFulfilledResult<T>).value);
};

const isoWeekKey = (date: Date) =>
  `${getISOWeekYear(date)}-${getISOWeek(date)}`;

const fetchOk = async (url: string): Promise<Response> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status}`);
  }
  return response;
};
